import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminCreateProduct, getAdminGetProductsQueryKey } from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

/**
 * Dialog for adding a new product
 */
export function AddProductDialog() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [displayName, setDisplayName] = useState('');

  const { mutate: createProduct, isPending: isCreating } = useAdminCreateProduct({
    mutation: {
      onSuccess: () => {
        toast.success('Product created successfully');
        setIsOpen(false);
        setProductId('');
        setDisplayName('');
        // Invalidate the products query to refetch
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductsQueryKey(),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'CreateProduct',
          productId: productId.trim(),
        });
        toast.error('Failed to create product. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  /**
   * Handles the add product action
   */
  const handleAdd = () => {
    createProduct({
      data: {
        productId: productId.trim(),
        displayName: displayName.trim(),
      },
    });
  };

  /**
   * Handles dialog close and resets form
   */
  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setProductId('');
      setDisplayName('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Product</DialogTitle>
          <DialogDescription>
            Create a new product with a unique identifier
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="productId">Product ID</Label>
            <Input
              id="productId"
              type="text"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              placeholder="my-product"
              maxLength={100}
            />
            <p className="text-sm text-muted-foreground">
              A unique identifier for the product (max 100 characters)
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="My Product"
              maxLength={100}
            />
            <p className="text-sm text-muted-foreground">
              The display name shown to users (max 100 characters)
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={isCreating || !productId.trim() || !displayName.trim()}>
            {isCreating ? 'Creating...' : 'Create Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
